import uuid from 'uuid/v1';
import service, { IVendor } from '@/services/vendor';
import componentService from '@/services/component';
import materialService from '@/services/material';
import * as priceFragments from '@/services/price/fragments';
import { store as DirtyCheckComposer } from '@/composers/dirty-check';
import { diffIdsRemoved, promiseError } from '@/store/utils';
import {
  INewResource,
  IStateVendor,
  IStateVendorResources,
} from './types';

const formatData = (data: IVendor): IVendor => (
  JSON.parse(
    JSON.stringify({
      name: '',
      notes: '',
      ...data,
      components: data.components || [],
      materials: data.materials || [],
      prices: data.prices || [],
      contact: data.contact || {
        address: '',
        country: '',
        email: '',
        phoneNumber: '',
        website: '',
      },
    }),
  )
);

export default () => DirtyCheckComposer({
  namespaced: true,
  state: () => ({
    data: {},
    saved: {},
    resources: {},
  } as IStateVendor),
  mutations: {
    ADD_NEW_RESOURCE(state: any, {
      typePlural,
      typeSingle,
      resource,
    }: INewResource) {
      // add to users resources
      state.resources[typePlural] = state.resources[typePlural].concat(resource);

      // add to vendor's resources
      const newResource = {
        id: uuid(),
        properties: [],
        [typeSingle]: resource,
      };

      // this is already stored on the server
      // so update data and saved. prevent isDirty
      state.data[typePlural] = state.data[typePlural].concat(newResource);
      state.saved[typePlural] = state.saved[typePlural].concat(newResource);
    },
    CREATE() {
      //
    },
    LOAD(state: IStateVendor, vendor: IVendor) {
      state.data = formatData(vendor);
      state.saved = formatData(vendor);
    },
    LOAD_RESOURCES(
      state: IStateVendor,
      resources: IStateVendorResources,
    ) {
      state.resources = resources;
    },
    REMOVE(state: IStateVendor) {
      // reset all data (prevents isDirty from being true)
      state.data = formatData({} as IVendor);
      state.saved = formatData({} as IVendor);
    },
    SAVE(state: IStateVendor) {
      state.saved = {
        ...JSON.parse(
          JSON.stringify(state.data),
        ),
      };
    },
  },
  actions: {
    create: ({ commit }: any) => (
      service.create({
        name: 'Vendor name',
      })
      .then(created => {
        commit('CREATE', created);
        return created;
      })
      .catch(promiseError('VENDOR_CREATE', commit))
    ),
    createResource: (
      { commit, state }: any,
      { type, name }: any,
    ) => (
      (type === 'component'
        ? componentService
        : materialService
      ).create({
        // create resource linked to current vendor
        name,
        vendors: {
          data: [{
            vendor_id: state.data.id,
          }],
        },
      })
      .then((newResource: any) => {
        commit('ADD_NEW_RESOURCE', {
          typePlural: `${type}s`,
          typeSingle: type,
          resource: {
            id: newResource.id,
            name,
          },
        } as INewResource);
        return newResource;
      })
    ),
    load: ({ commit }: any, id: string) => (
      Promise.all([
        componentService.find(`id, name`),
        materialService.find(`id, name`),
        service
          .get(id, `
            id
            name
            contact
            notes
            materials {
              id
              properties
              material {
                id
                name
              }
            }
            components {
              id
              properties
              component {
                id
                name
              }
            }
            ${priceFragments.query()}
          `),
      ]).then((result: any) => {
        const [ components, materials, listed ] = result;
        commit('LOAD', listed);
        commit('LOAD_RESOURCES', {
          components: components.result,
          materials: materials.result,
        });
        return listed;
      })
      .catch(promiseError('VENDOR_LOAD', commit))
    ),
    remove: ({ commit }: any, id: string) => (
      service
        .remove(id)
        .then(() => commit('REMOVE'))
        .catch(promiseError('VENDOR_REMOVE', commit))
    ),
    save: ({ commit, state }: any) => {
      const { data, saved } = state;
      const deleteComponentIds = diffIdsRemoved(data.components, saved.components);
      const deleteMaterialIds = diffIdsRemoved(data.materials, saved.materials);
      return service
        .update(data.id, {
          vendor: data,
          deleteComponentIds,
          deleteMaterialIds,

          // add pricing payload
          priceRemoveIds: diffIdsRemoved(data.prices, saved.prices),
          prices: data.prices.map((price: any) => ({
            ...price,
            type: 'vendor',
            type_id: data.id,
          })),
        })
        .then(() => commit('SAVE'))
        .catch(promiseError('VENDOR_SAVE', commit));
    },
  },
});
